import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';

declare global {
  interface Window { gtag?: (...args: unknown[]) => void; }
}
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Check, X, MailCheck, Shield, FileText, DollarSign, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { api } from '../../services/api';
import { API_BASE_URL } from '../../config/api';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../../layouts/AuthLayout';

interface RegisterFormData {
  email: string;
  password: string;
  password_confirmation: string;
  website_url: string; // honeypot field — must remain empty
  privacy_consent: boolean;
  marketing_consent: boolean;
}

const Register: React.FC = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<RegisterFormData>({
    email: searchParams.get('email') || '',
    password: '',
    password_confirmation: '',
    website_url: '',
    privacy_consent: false,
    marketing_consent: false,
  });
  const [emailChecking, setEmailChecking] = useState(false);
  const emailCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const passwordRules = useMemo(() => {
    const pw = formData.password;
    return {
      min: pw.length >= 8,
      uppercase: /[A-Z]/.test(pw),
      lowercase: /[a-z]/.test(pw),
      special: /[^A-Za-z0-9]/.test(pw),
    };
  }, [formData.password]);

  const allRulesMet = passwordRules.min && passwordRules.uppercase && passwordRules.lowercase && passwordRules.special;

  const canSubmit =
    formData.email.trim() !== '' &&
    /\S+@\S+\.\S+/.test(formData.email) &&
    allRulesMet &&
    formData.password === formData.password_confirmation &&
    formData.password_confirmation.length > 0 &&
    formData.privacy_consent;

  const validateEmailDomain = useCallback(async (email: string) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    setEmailChecking(true);
    try {
      const res: any = await api.post('/customer/validate-email', { email });
      if (res?.valid === false || res?.data?.valid === false) {
        setValidationErrors(prev => ({ ...prev, email: t('register.error_invalid_email') }));
      }
    } catch {
      // Silently ignore — server-side will catch on submit
    } finally {
      setEmailChecking(false);
    }
  }, [t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setError(null);

    // Debounced email validation (800ms after user stops typing)
    if (name === 'email') {
      if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
      emailCheckTimer.current = setTimeout(() => validateEmailDomain(value), 800);
    }
  };

  // Fire Google Ads conversion event on successful account creation
  useEffect(() => {
    if (success) {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18047556304/_WuCCL-DzZEcENC1351D',
          'value': 0.0,
          'currency': 'CAD'
        });
      }
    }
  }, [success]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setValidationErrors({});

    if (!canSubmit) {
      setLoading(false);
      return;
    }

    try {
      const { website_url, privacy_consent, marketing_consent, ...submitData } = formData;
      await api.post('/customer/register', {
        ...submitData,
        website_url,
        privacy_consent: privacy_consent ? '1' : '',
        tos_consent: privacy_consent ? '1' : '',
        marketing_consent,
      });
      setSuccess(true);
    } catch (err: any) {
      if (err.errors) {
        setValidationErrors(
          Object.fromEntries(
            Object.entries(err.errors).map(([key, val]) => [key, Array.isArray(val) ? val[0] : val])
          ) as Record<string, string>
        );
      } else {
        setError(err.message || t('register.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border dark:border-slate-700 p-6 sm:p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
              <MailCheck className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">{t('register.success_title')}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t('register.success_message')}</p>
          <Link to="/login">
            <Button className="w-full">{t('register.success_back_to_login')}</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border dark:border-slate-700 p-6 sm:p-8">
        <h2 className="text-2xl font-bold text-center mb-2">{t('register.title')}</h2>
        <p className="text-sm text-muted-foreground text-center mb-4">{t('register.subtitle')}</p>

        {/* Trust Strip */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Shield className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span>{t('register.trust_no_card')}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileText className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span>{t('register.trust_unlimited')}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <DollarSign className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span>{t('register.trust_no_fees')}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Zap className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span>{t('register.trust_ready')}</span>
          </div>
        </div>

        <div className="space-y-4">
          {/* SSO Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="w-full flex items-center gap-2 h-12 text-sm font-semibold" onClick={() => {
              const backendUrl = API_BASE_URL.replace(/\/api\/?$/, '');
              window.location.href = `${backendUrl}/auth/microsoft`;
            }}>
              <Microsoft className="h-5 w-5" />
              {t('login.microsoft_button')}
            </Button>

            <Button variant="outline" className="w-full flex items-center gap-2 h-12 text-sm font-semibold" onClick={() => {
              const backendUrl = API_BASE_URL.replace(/\/api\/?$/, '');
              window.location.href = `${backendUrl}/auth/google`;
            }}>
              <Google className="h-5 w-5" />
              {t('login.google_button')}
            </Button>
          </div>

          {/* Separator */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white dark:bg-slate-900 px-2 text-xs text-muted-foreground">
                {t('register.signup_separator')}
              </span>
            </div>
          </div>

          {/* Email & Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-3 rounded-md text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* Honeypot — hidden from humans, bots will fill it */}
            <input
              type="text"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
            />

            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">{t('register.your_email')}</label>
              <Input
                type="email"
                name="email"
                placeholder={t('login.email_placeholder')}
                value={formData.email}
                onChange={handleChange}
                className={validationErrors.email ? 'border-red-500' : ''}
                disabled={loading}
              />
              {validationErrors.email && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationErrors.email}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">{t('register.password')}</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t('login.password_placeholder')}
                  value={formData.password}
                  onChange={handleChange}
                  className={validationErrors.password ? 'border-red-500 pr-10' : 'pr-10'}
                  disabled={loading}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-2.5 text-muted-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {formData.password.length > 0 && (
                <ul className="space-y-0.5 mt-1">
                  {[
                    { key: 'min', label: t('register.password_rule_min') },
                    { key: 'uppercase', label: t('register.password_rule_uppercase') },
                    { key: 'lowercase', label: t('register.password_rule_lowercase') },
                    { key: 'special', label: t('register.password_rule_special') },
                  ].map(({ key, label }) => (
                    <li key={key} className={`text-xs flex items-center gap-1 ${passwordRules[key as keyof typeof passwordRules] ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {passwordRules[key as keyof typeof passwordRules] ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      {label}
                    </li>
                  ))}
                </ul>
              )}
              {validationErrors.password && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationErrors.password}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">{t('register.confirm_password')}</label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="password_confirmation"
                  placeholder={t('register.confirm_password_placeholder')}
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  className={validationErrors.password_confirmation ? 'border-red-500 pr-10' : 'pr-10'}
                  disabled={loading}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-2.5 text-muted-foreground"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {validationErrors.password_confirmation && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationErrors.password_confirmation}
                </p>
              )}
              {!validationErrors.password_confirmation && formData.password_confirmation.length > 0 && formData.password !== formData.password_confirmation && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {t('register.password_mismatch')}
                </p>
              )}
            </div>

            {/* Privacy Consent (required) */}
            <div className="space-y-2">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.privacy_consent}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, privacy_consent: e.target.checked }));
                    if (validationErrors.privacy_consent) {
                      setValidationErrors(prev => { const next = { ...prev }; delete next.privacy_consent; return next; });
                    }
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-slate-600">{t('register.privacy_consent_text')}</span>
              </label>
              {validationErrors.privacy_consent && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationErrors.privacy_consent}
                </p>
              )}

              {/* Marketing Consent (optional) */}
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.marketing_consent}
                  onChange={(e) => setFormData(prev => ({ ...prev, marketing_consent: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-slate-500">{t('register.marketing_consent_label')}</span>
              </label>
            </div>

            <Button type="submit" className="w-full" disabled={loading || emailChecking || !canSubmit}>
              {loading ? t('register.creating_account') : t('register.create_account')}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">{t('register.microcopy')}</p>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {t('register.signin_prompt')}{' '}
            <Link to="/login" className="text-primary hover:underline">{t('register.signin_link')}</Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Register;

function Microsoft(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
    >
      <path d="M11.4 2.8H2.8V11.4H11.4V2.8Z" fill="#F25022"/>
      <path d="M11.4 12.6H2.8V21.2H11.4V12.6Z" fill="#00A4EF"/>
      <path d="M21.2 2.8H12.6V11.4H21.2V2.8Z" fill="#7FBA00"/>
      <path d="M21.2 12.6H12.6V21.2H21.2V12.6Z" fill="#FFB900"/>
    </svg>
  );
}

function Google(props: any) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}
